import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE, headers: { "Content-Type": "application/json" } });

export { api };

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use((res) => res, (err) => {
  // Normalize FastAPI 422 ValidationError: detail can be an array of {type, loc, msg, input, url}
  // Convert to a friendly string so toast.error / direct rendering never explodes React.
  try {
    const d = err?.response?.data?.detail;
    if (Array.isArray(d)) {
      const msg = d.map((e) => {
        if (!e || typeof e !== "object") return String(e ?? "");
        const field = Array.isArray(e.loc) ? e.loc.filter(Boolean).slice(-1)[0] : "";
        return field ? `${field}: ${e.msg || "error"}` : (e.msg || "error");
      }).filter(Boolean).join(" · ") || "Datos no válidos";
      err.response.data.detail = msg;
    } else if (d && typeof d === "object") {
      err.response.data.detail = d.msg || d.message || JSON.stringify(d).slice(0, 200);
    }
  } catch { /* no-op */ }
  if (err.response?.status === 401) {
    // Special case: La Armería endpoints use 401 as an intentional signal
    // ("no logged-in user → show signup modal"), NOT as "session expired".
    // We must NOT clear localStorage or redirect — let the caller handle it.
    const url = (err.config?.url || "");
    if (url.includes("/armeria/")) {
      return Promise.reject(err);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/";
  }
  return Promise.reject(err);
});

export const authApi = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
  tierAccess: () => api.get("/user/tier-access"),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (email, code, new_password) => api.post("/auth/reset-password", { email, code, new_password }),
};

export const articlesApi = {
  generate: (data) => api.post("/articles/generate", data),
  generateStatus: (jobId) => api.get(`/articles/generate-status/${jobId}`),
  list: () => api.get("/articles"),
  stats: () => api.get("/articles/stats"),
  get: (id) => api.get(`/articles/${id}`),
  update: (id, data) => api.put(`/articles/${id}`, data),
  delete: (id) => api.delete(`/articles/${id}`),
  generateImage: (id, overrides) => api.post(`/articles/${id}/generate-image`, overrides || {}),
  uploadImage: (id, formData) => api.post(`/articles/${id}/upload-image`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  generateSocial: (id, networks) => api.post(`/articles/${id}/generate-social`, networks ? { networks } : {}),
  socialStatus: (jobId) => api.get(`/articles/social-status/${jobId}`),
  generateSocialImage: (data) => api.post("/articles/generate-social-image", data),
  socialImageStatus: (jobId) => api.get(`/articles/social-image-status/${jobId}`),
  extractPhrases: (id, count = 5) => api.post(`/articles/${id}/extract-phrases`, { count }),
  generateSocialImages: (id, payload) => api.post(`/articles/${id}/generate-social-images`, payload),
  socialImagesStatus: (id, jobId) => api.get(`/articles/${id}/social-images-status/${jobId}`),
  downloadSocialImagesZip: (id, formats) => api.post(`/articles/${id}/download-social-images-zip`, { formats }, { responseType: "blob" }),
  publishWordPress: (id, status, scheduledDate, categories, tags) => api.post(`/articles/${id}/publish-wordpress`, { status: status || "draft", scheduled_date: scheduledDate || null, categories: categories || [], tags: tags || [] }),
  publishSocial: (id) => api.post(`/articles/${id}/publish-social`),
  download: (id, format) => api.get(`/articles/${id}/download/${format}`),
  calendar: (year, month) => api.get(`/articles/calendar/${year}/${month}`),
};

export const imageApi = {
  styles: () => api.get("/image-styles"),
};

export const userSettingsApi = {
  get: () => api.get("/user/settings"),
  update: (data) => api.put("/user/settings", data),
  uploadLogo: (formData) => api.post("/user/upload-logo", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  destinations: () => api.get("/user/destinations"),
};

export const publishApi = {
  history: () => api.get("/publish/history"),
};

export const subscriptionApi = {
  status: () => api.get("/subscription/status"),
  cancel: () => api.post("/subscription/cancel"),
};

export const voiceProfileApi = {
  get: () => api.get("/user/voice-profile"),
  update: (data) => api.put("/user/voice-profile", data),
  analyzeVoice: (formData) => api.post("/user/analyze-voice", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  analysisStatus: (jobId) => api.get(`/user/voice-analysis-status/${jobId}`),
};

export const creditsApi = {
  config: () => api.get("/credits/config"),
  balance: () => api.get("/credits/balance"),
  history: () => api.get("/credits/history"),
};

export const resourcesApi = {
  list: () => api.get("/resources"),
  get: (id) => api.get(`/resources/${id}`),
  upload: (formData) => api.post("/admin/resources/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  delete: (id) => api.delete(`/admin/resources/${id}`),
};

export const wordpressApi = { status: () => api.get("/wordpress/status") };

export const demoApi = { article: () => api.get("/demo/article") };

export default api;
